import React, { useState, useEffect, useRef } from 'react';

const AnchorSVG = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="20" cy="7" r="2" fill="currentColor"/>
    <line x1="4" y1="14" x2="36" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="20" y1="12" x2="20" y2="40" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M20 40 C13 38 5 33 5 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M20 40 C27 38 35 33 35 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M5 26 C5 46 35 46 35 26" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

const chapters = [
  { id: 'how-we-met',       label: 'How We Met' },
  { id: 'long-distance',    label: 'Long Distance' },
  { id: 'together-in-utah', label: 'Together in Utah' },
  { id: 'engagement',       label: 'Engagement' },
];

const Story: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeChapter, setActiveChapter] = useState('how-we-met');

  /* scroll spy for chapter nav */
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveChapter(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    chapters.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToChapter = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const offset = el.getBoundingClientRect().top + window.scrollY - 180;
    window.scrollTo({ top: offset, behavior: 'smooth' });
  };

  return (
    <main className="story-section">
      {/* PAGE HEADER */}
      <div className="page-header">
        <AnchorSVG className="page-header__anchor" />
        <p className="page-header__eyebrow">Ashley &amp; A.J.</p>
        <h1 className="page-header__title">Our Story</h1>
        <div className="page-header__rule page-rule">
          <div className="rule-line rule-line--short"></div>
          <div className="rule-diamond"></div>
          <div className="rule-line rule-line--short"></div>
        </div>
      </div>

      {/* CHAPTER NAV */}
      <nav className="chapter-nav">
        {chapters.map(({ id, label }) => (
          <button
            key={id}
            className={`chapter-nav__link ${activeChapter === id ? 'chapter-nav__link--active' : ''}`}
            onClick={() => scrollToChapter(id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* CONTENT */}
      <div className="story-content">
        <div className="story-inner">

          {/* ── CHAPTER 1 ── */}
          <div className="chapter" id="how-we-met">
            <p className="chapter__number">Chapter One</p>
            <h2 className="chapter__title">How We Met</h2>
            <div className="chapter__rule page-rule">
              <div className="rule-line rule-line--short" style={{ width: 40 }}></div>
              <div className="rule-diamond"></div>
            </div>
            <p className="chapter__body">
              Ashley and A.J. first crossed paths at Michigan State University during a student ministry hangout in 2020,
              but their story didn't truly begin until two years later. Ashley met A.J.'s sister, Maria, on a spring break
              mission trip. After seeing their fun social media post together, A.J. decided to join Maria at Ashley's dance
              performance at MSU. That night, Ashley and A.J. reconnected, started chatting on Instagram, and soon took
              their conversations to real life — starting with coffee at Blue Owl, followed by an impromptu first date
              where they went thrifting and ate sushi. After many more hangouts, late night Hello Fresh cooking parties,
              and dates, they made it official at Biggby on April 18, 2022, and the rest is history.
            </p>
            <div className="story-images">
              <img src="/IMG_2760.jpg"  alt="Together"         className="story-image" onClick={() => setSelectedImage('/IMG_2760.jpg')}  loading="lazy" />
              <img src="/IMG_3245.jpg"  alt="College memories" className="story-image" onClick={() => setSelectedImage('/IMG_3245.jpg')}  loading="lazy" />
              <img src="/IMG_6377.jpg"  alt="Early dates"      className="story-image" onClick={() => setSelectedImage('/IMG_6377.jpg')}  loading="lazy" />
              <img src="/IMG_9263.jpg"  alt="Ashley and A.J."  className="story-image" onClick={() => setSelectedImage('/IMG_9263.jpg')}  loading="lazy" />
              <img src="/IMG_2687.jpg"  alt="Making it official" className="story-image" onClick={() => setSelectedImage('/IMG_2687.jpg')} loading="lazy" />
              <img src="/IMG_1827.jpg"  alt="MSU days"         className="story-image" onClick={() => setSelectedImage('/IMG_1827.jpg')}  loading="lazy" />
            </div>
          </div>

          {/* ── CHAPTER 2 ── */}
          <div className="chapter" id="long-distance">
            <p className="chapter__number">Chapter Two</p>
            <h2 className="chapter__title">Long Distance</h2>
            <div className="chapter__rule page-rule">
              <div className="rule-line rule-line--short" style={{ width: 40 }}></div>
              <div className="rule-diamond"></div>
            </div>
            <p className="chapter__body">
              After Ashley graduated from Michigan State in May 2023, she moved to Salt Lake City to begin her Doctorate
              in Occupational Therapy at the University of Utah. For the next seven months, they did long distance —
              filling the miles between them with FaceTime date nights and phone calls — until A.J. graduated, landed a
              job as a remote Full-Stack Engineer, and moved to Salt Lake City as well.
            </p>
            <div className="story-images">
              <img src="/IMG_5688.jpg"   alt="Long distance"         className="story-image" onClick={() => setSelectedImage('/IMG_5688.jpg')}   loading="lazy" />
              <img src="/IMG_84888.jpeg" alt="FaceTime cooking"      className="story-image" onClick={() => setSelectedImage('/IMG_84888.jpeg')} loading="lazy" />
              <img src="/untitled4.jpg"  alt="FaceTime dates"        className="story-image" onClick={() => setSelectedImage('/untitled4.jpg')}  loading="lazy" />
              <img src="/IMG_9027.JPG"   alt="Long distance memories" className="story-image" onClick={() => setSelectedImage('/IMG_9027.JPG')}  loading="lazy" />
              <img src="/IMG_9096.jpg"   alt="Long distance moments"  className="story-image" onClick={() => setSelectedImage('/IMG_9096.jpg')}  loading="lazy" />
              <img src="/72200151189.jpg" alt="Long distance times"   className="story-image" onClick={() => setSelectedImage('/72200151189.jpg')} loading="lazy" />
              <img src="/IMG_8303.jpeg"  alt="Long distance journey"  className="story-image" onClick={() => setSelectedImage('/IMG_8303.jpeg')} loading="lazy" />
            </div>
          </div>

          {/* ── CHAPTER 3 ── */}
          <div className="chapter" id="together-in-utah">
            <p className="chapter__number">Chapter Three</p>
            <h2 className="chapter__title">Together in Utah</h2>
            <div className="chapter__rule page-rule">
              <div className="rule-line rule-line--short" style={{ width: 40 }}></div>
              <div className="rule-diamond"></div>
            </div>
            <p className="chapter__body">
              Since moving to Utah, they have found a wonderful community and shared countless outdoor adventures including
              snowboarding, hiking, paddleboarding, and national park road trips! A.J. also completed his first marathon.
              They have continued to grow in their faith, actively serving at their church. For the first two years in
              Utah, Ashley worked full time in the hospital burn unit while completing her doctorate program, developing a
              capstone project focused on music-based occupational therapy interventions in Morocco. On May 1st, 2026,
              Ashley officially earned her Doctorate in Occupational Therapy, and after the wedding, she plans to begin
              her next adventure as a Travel Occupational Therapist, with A.J. continuing as a remote Full-Stack Engineer.
            </p>
            <div className="story-images">
              <img src="/IMG_0242.jpg"  alt="Graduation"      className="story-image" onClick={() => setSelectedImage('/IMG_0242.jpg')}  loading="lazy" />
              <img src="/IMG_1071.jpg"  alt="Salt Lake City"  className="story-image" onClick={() => setSelectedImage('/IMG_1071.jpg')}  loading="lazy" />
              <img src="/IMG_1552.jpg"  alt="Ashley's move"   className="story-image" onClick={() => setSelectedImage('/IMG_1552.jpg')}  loading="lazy" />
              <img src="/IMG_1894.jpg"  alt="New adventures"  className="story-image" onClick={() => setSelectedImage('/IMG_1894.jpg')}  loading="lazy" />
              <img src="/IMG_8559.jpg"  alt="National parks"  className="story-image" onClick={() => setSelectedImage('/IMG_8559.jpg')}  loading="lazy" />
              <img src="/IMG_2328.jpg"  alt="Utah adventures" className="story-image" onClick={() => setSelectedImage('/IMG_2328.jpg')}  loading="lazy" />
              <img src="/IMG_2393.jpg"  alt="Snowboarding"    className="story-image" onClick={() => setSelectedImage('/IMG_2393.jpg')}  loading="lazy" />
              <img src="/IMG_2887.jpg"  alt="Hiking"          className="story-image" onClick={() => setSelectedImage('/IMG_2887.jpg')}  loading="lazy" />
              <img src="/IMG_2983.jpg"  alt="Marathon"        className="story-image" onClick={() => setSelectedImage('/IMG_2983.jpg')}  loading="lazy" />
              <img src="/IMG_9990.jpg"  alt="Utah life"       className="story-image" onClick={() => setSelectedImage('/IMG_9990.jpg')}  loading="lazy" />
              <img src="/IMG_9651.jpeg" alt="Utah memories"   className="story-image" onClick={() => setSelectedImage('/IMG_9651.jpeg')} loading="lazy" />
            </div>
          </div>

          {/* ── CHAPTER 4 ── */}
          <div className="chapter" id="engagement">
            <p className="chapter__number">Chapter Four</p>
            <h2 className="chapter__title">Engagement</h2>
            <div className="chapter__rule page-rule">
              <div className="rule-line rule-line--short" style={{ width: 40 }}></div>
              <div className="rule-diamond"></div>
            </div>
            <p className="chapter__body">
              On July 17, 2025, A.J. proposed to Ashley at their favorite sunset spot. She obviously said yes, and they began planning their life together.
            </p>
            <div className="engagement-images">
              <img
                src="/368B47AF-B50B-4384-B1F3-CC01E05165B0_1_105_c.jpeg"
                alt="Engagement"
                className="engagement-full"
                onClick={() => setSelectedImage('/368B47AF-B50B-4384-B1F3-CC01E05165B0_1_105_c.jpeg')}
                loading="lazy"
              />
              <div className="engagement-row">
                <img src="/AB4A9542_Original.jpg.jpeg" alt="The proposal" onClick={() => setSelectedImage('/AB4A9542_Original.jpg.jpeg')} loading="lazy" />
                <img src="/AB4A9682_Original.jpg.jpeg" alt="Engaged"      onClick={() => setSelectedImage('/AB4A9682_Original.jpg.jpeg')} loading="lazy" />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* IMAGE MODAL */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <button className="close-button" onClick={() => setSelectedImage(null)}>&times;</button>
          <img src={selectedImage} alt="Full size" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </main>
  );
};

export default Story;
